import { useCallback, useEffect, useState } from "react";
import { Switch, Route, Redirect, useHistory, useRouteMatch, useLocation } from "react-router-dom";

import UserContext from "../context/UserContext";
import UserUpdateContext from "../context/UserUpdateContext";
import CrewContext from "../context/CrewContext";

import Dashboard from "./dashboard";
import AuthErrorScreen from "../components/AuthErrorScreen";
import { isLoggedIn } from "../services/auth";
import { getMyInfo } from "../api/user";
import { getDefaultSettings, saveDefaultSettings } from "../api/settings";
import { SettingKey } from "../shared/api-types/user";
import { initializeHistory } from "../services/navigation";
import { setGrowthBookAttributes } from "../services/growthbook";
import { applyActiveLanguage } from "../services/localization";

// initAuth uses onLoad "login-required", so the app only renders once signed in; should the session still be missing,
// show the sign-in error screen rather than an empty page (and never start a login loop from a render).
export const PrivateRoute = ({ children, ...props }) => {
  const userLoggedIn = isLoggedIn();
  return (
    <Route
      render={() =>
        userLoggedIn ? (
          <Route {...props}>{children}</Route>
        ) : (
          <AuthErrorScreen reason="not_authenticated" onRetry={() => window.location.reload()} />
        )
      }
    />
  );
};

export const WithUserContext = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();
  const crewIdFromRoute = match?.params?.crewId;
  const [user, setUser] = useState(null);
  const [activeCrew, setActiveCrew] = useState(null);

  // Initialize history for navigation service
  useEffect(() => {
    initializeHistory(history);
  }, [history]);

  const updateUser = useCallback((patch) => setUser((current) => (current ? { ...current, ...patch } : current)), []);

  const isSuperAdmin = user?.crews?.some((c) => c.id === 0) ?? false;

  const setCrew = (crew) => {
    // only for superadmin:
    // if there's a param in route other than crewId, go to home
    // if crewId: replace with new crewId
    let path;
    if (isSuperAdmin) {
      const keys = Object.keys(match?.params || {});
      if (!match || keys.length > 1 || (keys.length && keys[0] !== "crewId")) {
        path = "/";
      } else {
        // stay where we are
        if (match.params.crewId != null) {
          // replace crewId
          path = history.location.pathname.replace(match.url, match.path.replace(":crewId", crew.id));
          // any other numeric params left in the url?
          const m = /\/(\d+)\/?/g.exec(path);
          if (m.length > 2) {
            // we bail, can't tell what those might be without the exact route match
            path = "/";
          }
        }
      }
    }
    setActiveCrew(crew);
    if (path && path !== history.location.pathname) {
      // Use replace instead of push to avoid triggering useEffect again
      // and preserve search params by using location.search
      const urlParams = new URLSearchParams(location.search);
      const companyIdFromUrl = urlParams.get("companyId");
      // Only preserve companyId if we're navigating to a different crew
      const searchString = companyIdFromUrl && crew.globalId !== companyIdFromUrl ? "" : location.search;
      history.replace(path + searchString);
    }
    if (!isSuperAdmin) {
      saveDefaultSettings(SettingKey.COMPANY_SELECTOR, { crewId: crew.id, baseIds: crew.baseIds ?? null });
    }

    // The "localization" flag is per-crew, so re-point GrowthBook at the new
    // crew and re-resolve the language: switching to a flag-enabled crew applies
    // the user's profile language; switching to a flag-disabled one reverts to
    // English.
    if (crew.id != null && user) {
      setGrowthBookAttributes(String(user.id), String(crew.id)).then(() => applyActiveLanguage(user.language));
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const companyIdFromUrl = urlParams.get("companyId");

    // Skip if user is already loaded and no companyId in URL (prevents resetting on re-render)
    if (!companyIdFromUrl && user) {
      return;
    }

    getMyInfo()
      .then((userData) =>
        Promise.all([
          userData,
          userData.crews?.some((c) => c.id === 0)
            ? Promise.resolve(null)
            : getDefaultSettings(SettingKey.COMPANY_SELECTOR),
        ]),
      )
      .then(async ([userData, savedSelector]) => {
        const isSuperAdminUser = userData.crews?.some((c) => c.id === 0) ?? false;
        const savedCrewId = savedSelector?.value?.crewId ?? null;
        const savedBaseIds = savedSelector?.value?.baseIds ?? null;

        let crew;
        // if companyId is provided in url, select crew based on it
        if (companyIdFromUrl) {
          crew = userData.crews.find((c) => c.globalId === companyIdFromUrl);
        }
        // otherwise select crew based on crewId from url
        if (!crew && crewIdFromRoute) {
          crew = userData.crews.find((c) => c.id === +crewIdFromRoute);
        }
        // super admin with no url override always gets the system crew (id: 0, first in list)
        if (!crew && isSuperAdminUser) {
          crew = userData.crews[0];
        }
        // otherwise select crew based on saved settings
        if (!crew && savedCrewId != null) {
          crew = userData.crews.find((c) => c.id === +savedCrewId);
        }
        // otherwise select first crew with active subscription
        if (!crew) {
          crew = userData.crews.find((c) => c.subscription !== "inactive");
        }
        // if no crew with subscription found, select first crew and show payment required page
        if (!crew) {
          crew = userData.crews[0];
        }
        // Restore saved baseIds only when the saved crewId matches the selected crew.
        // For superadmins, bases are never restored (settings are not used for them).
        if (crew) {
          let baseName = null;
          const baseIdsToRestore = !isSuperAdminUser && crew.id === savedCrewId ? savedBaseIds : null;
          if (baseIdsToRestore?.length && crew.bases) {
            const selectedBaseNames = baseIdsToRestore
              .map((id) => crew.bases.find((b) => b.id === +id)?.name)
              .filter(Boolean);
            baseName = selectedBaseNames.join(", ") || null;
          }
          crew = { ...crew, baseIds: baseIdsToRestore, baseName };

          // Persist the resolved crew when it differs from what is stored (non-superadmins only),
          // so the next load without a URL param restores the correct crew.
          if (!isSuperAdminUser && crew.id !== savedCrewId) {
            saveDefaultSettings(SettingKey.COMPANY_SELECTOR, {
              crewId: crew.id,
              baseIds: null,
            });
          }
        }
        const resolvedCrew = crew || { name: userData.fullname };

        // Set GrowthBook attributes as soon as the crew is resolved, so feature
        // flags (which key off crewId/userId) evaluate correctly here — and not
        // a render later. Awaited so growthbook.isOn below sees the attributes.
        if (resolvedCrew.id != null) {
          await setGrowthBookAttributes(String(userData.id), String(resolvedCrew.id));
        }

        // Apply the user's profile language — gated behind the "localization"
        // flag, which now evaluates correctly because attributes are set above.
        applyActiveLanguage(userData.language);

        setActiveCrew(resolvedCrew);
        setUser(userData);
      })
      .catch(() => {
        setUser(false);
      });
  }, [location.search]);

  return user ? (
    <UserContext.Provider value={user}>
      <UserUpdateContext.Provider value={updateUser}>
        <CrewContext.Provider
          value={{
            crew: activeCrew,
            setCrew,
          }}
        >
          {children}
        </CrewContext.Provider>
      </UserUpdateContext.Provider>
    </UserContext.Provider>
  ) : user !== false ? null : (
    <Redirect to="/" />
  );
};

const Routes = () => (
  <Switch>
    <PrivateRoute path={["/crew/:crewId", "/"]}>
      <WithUserContext>
        <Dashboard />
      </WithUserContext>
    </PrivateRoute>
  </Switch>
);

export default Routes;
